import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ControlLogo } from "@/components/ControlLogo";
import { LayoutDashboard, Users, Settings, ScrollText, Activity, LogOut, Menu, ShieldCheck, Lock, Eraser } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin", label: "Oversikt", icon: LayoutDashboard, end: true, testId: "admin-nav-dashboard" },
  { to: "/admin/brukere", label: "Brukere", icon: Users, testId: "admin-nav-users" },
  { to: "/admin/innstillinger", label: "Systeminnstillinger", icon: Settings, testId: "admin-nav-settings" },
  { to: "/admin/revisjonslogg", label: "Revisjonslogg", icon: ScrollText, testId: "admin-nav-audit" },
  { to: "/admin/drift", label: "Drift og helse", icon: Activity, testId: "admin-nav-health" },
  { to: "/admin/opprydding", label: "Opprydding", icon: Eraser, testId: "admin-nav-cleanup" },
];

const Brand = () => (
  <Link to="/admin" className="flex items-center gap-2.5 px-1" data-testid="admin-brand" aria-label="Controlle administrasjon">
    <ControlLogo size={28} tagline="Administrasjon" />
  </Link>
);

const NavItems = ({ onNavigate }) => (
  <nav className="flex flex-col gap-1">
    {NAV.map((item) => (
      <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} data-testid={item.testId} className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors", isActive ? "bg-admin" : "hover:bg-secondary")}>
        <item.icon style={{ width: 18, height: 18 }} /> {item.label}
      </NavLink>
    ))}
  </nav>
);

export const AdminShell = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen app-canvas flex">
      <aside className="hidden lg:flex w-[260px] shrink-0 flex-col border-r bg-card px-4 py-5 gap-6 sticky top-0 h-screen">
        <Brand />
        <NavItems />
        <div className="mt-auto rounded-lg border bg-secondary p-3 text-xs text-muted-foreground">
          <p className="flex items-center gap-2 text-foreground font-medium mb-1"><Lock className="h-4 w-4" /> Kun metadata</p>
          Administrator ser aldri fakturainnhold, beløp, KID eller dokumenter. Bare aggregerte tall og hendelser.
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 border-b bg-card">
          <div className="flex items-center gap-3 px-4 sm:px-6 lg:px-8 h-16">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><Button variant="ghost" size="icon" className="lg:hidden" aria-label="Meny" data-testid="admin-mobile-menu"><Menu className="h-5 w-5" /></Button></SheetTrigger>
              <SheetContent side="left" className="bg-card w-[280px] p-4"><SheetTitle className="sr-only">Meny</SheetTitle><div className="mb-6"><Brand /></div><NavItems onNavigate={() => setOpen(false)} /></SheetContent>
            </Sheet>
            <div className="lg:hidden"><Brand /></div>
            <span className="ml-2 hidden sm:inline-flex items-center gap-1 rounded-full bg-admin px-2.5 py-1 text-xs font-medium" data-testid="admin-badge"><ShieldCheck className="h-3.5 w-3.5" /> Admin</span>
            <div className="ml-auto flex items-center gap-2">
              <span className="hidden md:inline text-sm text-muted-foreground">{user?.email}</span>
              <ThemeToggle />
              <Button variant="outline" size="sm" onClick={async () => { await logout(); navigate("/logg-inn"); }} aria-label="Logg ut" data-testid="admin-logout-button"><LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Logg ut</span></Button>
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 md:py-8"><div className="mx-auto max-w-[1200px] space-y-6"><Outlet /></div></main>
      </div>
    </div>
  );
};
